import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { apiErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordField } from "../components/ui/PasswordField";
import { ThemeToggle } from "../components/layout/ThemeToggle";

export function LoginPage(): JSX.Element {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={next} replace />;
  }

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    if (email.trim().length === 0 || password.length === 0) {
      toast.error("Preencha email e senha");
      return;
    }
    setSubmitting(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Bem-vindo(a), ${user.name}`);
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg)",
      }}
    >
      <header className="spread" style={{ padding: "1rem" }}>
        <span />
        <ThemeToggle />
      </header>
      <main
        className="stack"
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          gap: "1.5rem",
        }}
      >
        <div className="cluster" style={{ gap: ".625rem" }}>
          <img src="/brand/hiit-logo-1080.png" alt="Hiit" style={{ width: "2.5rem", height: "2.5rem" }} />
          <span style={{ fontSize: "1.25rem", fontWeight: 900 }}>Vitrine</span>
        </div>
        <div
          className="card"
          style={{
            width: "100%",
            maxWidth: "27.5rem",
            borderRadius: "var(--radius-xl)",
            boxShadow: "var(--shadow-card), inset 0 .3125rem 0 0 var(--color-coral)",
          }}
        >
          <div className="stack" style={{ gap: "0" }}>
            <h1 style={{ margin: 0, fontSize: "1.375rem", fontWeight: 900, letterSpacing: "-.02em" }}>
              Entrar
            </h1>
            <p style={{ margin: ".25rem 0 0", fontSize: ".8125rem", fontWeight: 600, color: "var(--color-ink-2)" }}>
              Acesse a Vitrine Hiit
            </p>
          </div>
          <form onSubmit={onSubmit} className="stack" style={{ marginTop: "1rem" }} noValidate>
            <label className="field" htmlFor="email">
              Email
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="email"
                autoFocus
                required
                disabled={submitting}
              />
            </label>
            <label className="field" htmlFor="password">
              Senha
              <PasswordField
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                disabled={submitting}
              />
            </label>
            <Button type="submit" loading={submitting} style={{ width: "100%" }}>
              Entrar
            </Button>
            <p style={{ textAlign: "center", fontSize: ".75rem", fontWeight: 700, color: "var(--color-ink-2)", margin: 0 }}>
              Acesso restrito a equipe Hiit Fitwear + Modulareasy
            </p>
          </form>
        </div>
        <div style={{ fontSize: ".6875rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
          Vitrine Hiit · v1.0.1
        </div>
      </main>
    </div>
  );
}
